import React, { useState, useEffect } from "react";
import Button from "./Button";
import Icon from "../AppIcon";

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [activeSection, setActiveSection] = useState("");

  const navigationItems = [
    {
      label: "Features",
      anchor: "benefits",
      description: "Explore mobile app features",
    },
    {
      label: "Screenshots",
      anchor: "screenshots",
      description: "See the app in action",
    },
    {
      label: "Reviews",
      anchor: "testimonials",
      description: "See what users are saying",
    },
    {
      label: "FAQ",
      anchor: "faq",
      description: "Common questions",
    },
  ];

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);

      // Update active section based on scroll position
      const sections = navigationItems?.map((item) => item?.anchor);
      const currentSection = sections?.find((section) => {
        const element = document.getElementById(section);
        if (element) {
          const rect = element?.getBoundingClientRect();
          return rect?.top <= 100 && rect?.bottom >= 100;
        }
        return false;
      });

      if (currentSection) {
        setActiveSection(currentSection);
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToSection = (anchor) => {
    const element = document.getElementById(anchor);
    if (element) {
      const headerHeight = 80;
      const elementPosition = element?.getBoundingClientRect()?.top;
      const offsetPosition =
        elementPosition + window.pageYOffset - headerHeight;

      window.scrollTo({
        top: offsetPosition,
        behavior: "smooth",
      });
    }
    setIsMobileMenuOpen(false);
  };

  const handleDownloadClick = () => {
    // Track conversion event
    if (window.gtag) {
      window.gtag("event", "click", {
        event_category: "CTA",
        event_label: "Header Download Button",
      });
    }

    // Scroll to download section
    const downloadSection = document.getElementById("app-download");
    if (downloadSection) {
      scrollToSection("app-download");
    }
  };

  return (
    <>
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-200 ${
          isScrolled
            ? "bg-white/95 backdrop-blur-sm shadow-sm border-b border-gray-100"
            : "bg-white"
        }`}
      >
        <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16 lg:h-20">
            {/* Logo */}
            <div className="flex-shrink-0">
              <button
                onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
                className="flex items-center space-x-2 group"
              >
                <img
                  src="/assets/logos/App_Icon.png"
                  alt="TAK Business Manager"
                  className="w-8 h-8 lg:w-10 lg:h-10 rounded-lg group-hover:opacity-90 transition-opacity"
                />
                <span className="text-xl lg:text-2xl font-bold text-text-primary">
                  TAK Business Manager
                </span>
              </button>
            </div>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center space-x-8">
              {navigationItems?.map((item) => (
                <button
                  key={item?.anchor}
                  onClick={() => scrollToSection(item?.anchor)}
                  className={`relative px-3 py-2 text-sm font-medium transition-colors duration-200 group ${
                    activeSection === item?.anchor
                      ? "text-primary"
                      : "text-text-secondary hover:text-text-primary"
                  }`}
                  title={item?.description}
                >
                  {item?.label}
                  {activeSection === item?.anchor && (
                    <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary rounded-full" />
                  )}
                </button>
              ))}

              <Button
                variant="default"
                size="default"
                onClick={handleDownloadClick}
                className="ml-4 shadow-cta"
                iconName="Download"
                iconPosition="left"
              >
                Download App
              </Button>
            </div>

            {/* Mobile Menu Button */}
            <div className="md:hidden">
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="p-2 rounded-lg text-text-secondary hover:text-text-primary hover:bg-gray-50 transition-colors"
                aria-label="Toggle mobile menu"
              >
                <Icon name={isMobileMenuOpen ? "X" : "Menu"} size={24} />
              </button>
            </div>
          </div>
        </nav>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="md:hidden bg-white border-t border-gray-100 shadow-lg">
            <div className="px-4 py-4 space-y-3">
              {navigationItems?.map((item) => (
                <button
                  key={item?.anchor}
                  onClick={() => scrollToSection(item?.anchor)}
                  className={`block w-full text-left px-3 py-3 rounded-lg text-base font-medium transition-colors ${
                    activeSection === item?.anchor
                      ? "text-primary bg-primary/5"
                      : "text-text-secondary hover:text-text-primary hover:bg-gray-50"
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <span>{item?.label}</span>
                    <Icon name="ChevronRight" size={16} />
                  </div>
                  <p className="text-sm text-text-secondary mt-1">
                    {item?.description}
                  </p>
                </button>
              ))}

              <div className="pt-4 border-t border-gray-100">
                <Button
                  variant="default"
                  size="lg"
                  fullWidth
                  onClick={handleDownloadClick}
                  className="shadow-cta"
                  iconName="Download"
                  iconPosition="left"
                >
                  Download App
                </Button>
              </div>
            </div>
          </div>
        )}
      </header>
      {/* Scroll Progress Indicator */}
      <div className="fixed top-16 lg:top-20 left-0 right-0 z-40">
        <div
          className="h-1 bg-primary transition-all duration-200 ease-out"
          style={{
            width: `${Math.min(
              100,
              (window.pageYOffset /
                (document.documentElement?.scrollHeight - window.innerHeight)) *
                100
            )}%`,
          }}
        />
      </div>
      {/* Floating Download CTA - Desktop */}
      <div className="hidden lg:block fixed bottom-8 right-8 z-40">
        <Button
          variant="default"
          size="lg"
          onClick={handleDownloadClick}
          className="shadow-cta animate-fade-in"
          iconName="Download"
          iconPosition="left"
        >
          Download App
        </Button>
      </div>

      {/* Floating Download CTA - Mobile */}
      <div className="lg:hidden fixed bottom-0 left-0 right-0 z-40 bg-white border-t border-gray-200 p-4">
        <Button
          variant="default"
          size="lg"
          fullWidth
          onClick={handleDownloadClick}
          className="shadow-cta"
          iconName="Download"
          iconPosition="left"
        >
          Download App
        </Button>
      </div>
    </>
  );
};

export default Header;
